import { Injectable, inject } from '@angular/core';
import { AwsApiWrapperService } from './api-wrapper.service';
import { from, mergeMap, Observable, of, pipe, throwError } from 'rxjs';
import { catchError, retryWhen, take, delay, concatMap, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { HttpErrorResponse } from '@angular/common/http';
import { SentryService } from '@brighthub/logging';

export interface GenericResponseResult {
  result: GenericResponseResultItems;
}

export interface GenericResponseResultItems {
  status: number;
  code: string;
  message: string;
  suggestion: string;
  suggestionCopyKey: string;
  docs: string;
}

export enum GenericResponseCode {
  SERVICE_OK = 'SERVICE_OK',
  MISSING_REQ_FIELD = 'MISSING_REQ_FIELD',
  INVALID_FIELD = 'INVALID_FIELD',
  SERVICE_ERROR = 'SERVICE_ERROR',
}

export interface APIOptionsInterface {
  retryCount?: number;
  cache?: boolean;
  errorCallback?(error: unknown): unknown;
}

abstract class APIWrapperClass {
  simulateError = false;

  defaultRetryCount = 3;
  private _messageService = inject(MessageService);
  private _sentry = inject(SentryService);

  constructor(
    protected awsAPIWrapper: AwsApiWrapperService
  ) {}

  protected _apiGet<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const initBody = init ? { body: { ...init } } : {};
    return from(this.awsAPIWrapper.get(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, init, 'get', options));
  }

  protected _apiPost<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const initBody = init ? { body: { ...init } } : {};
    return from(this.awsAPIWrapper.post(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, init, 'post', options));
  }

  protected _apiPatch<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const initBody = init ? { body: { ...init } } : {};
    return from(this.awsAPIWrapper.patch(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, init, 'patch', options));
  }

  protected _apiPut<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const initBody = init ? { body: { ...init } } : {};
    return from(this.awsAPIWrapper.put(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, init, 'put', options));
  }

  protected _apiDel<T>(
    apiName: string,
    path: string,
    init: any,
    options?: APIOptionsInterface
  ): Observable<T & GenericResponseResult> {
    const initBody = init ? { body: { ...init } } : {};
    return from(this.awsAPIWrapper.del(apiName, path, initBody)).pipe(this._apiPipe<T>(apiName, path, init, 'del', options));
  }

  // eslint-disable-next-line @typescript-eslint/member-ordering
  _apiPipe<T>(apiName: string, path: string, init: any, callType: string, options?: APIOptionsInterface) {
    this._sentry.addBreadcrumb({
      category: 'api',
      message: `Calling API: ${callType.toUpperCase()} ${path}`,
      level: 'info',
      data: {
        apiName,
        path,
        method: callType,
        requestBody: init,
      },
    });
    return pipe(
      retryWhen((errors) =>
        errors.pipe(
          delay(500),
          take(options?.retryCount ?? this.defaultRetryCount),
          concatMap((error) => throwError(() => error))
        )
      ),
      mergeMap<T & GenericResponseResult, Observable<T>>((response) => {
        const result = response.result;

        if (result.status === 403 && result.message === this.awsAPIWrapper.unauthMsg) {
          this.awsAPIWrapper.forceLogout();
        }

        if (result.status === 500) {
          return throwError(() => new Error(result.message));
        }

        return of(response); // allow the pipeline to continue
      }),
      catchError((error) =>
        this._APIErrorHandler(apiName, path, init, error, callType, options)
      )
    );
  }


  // eslint-disable-next-line @typescript-eslint/member-ordering
  _APIErrorHandler = (
    apiName: string,
    path: string,
    init: any,
    error: unknown,
    callType: string,
    options?: APIOptionsInterface | undefined
  ): Observable<any> => {
    const errorAs = error as GenericResponseResult & HttpErrorResponse;

    this._sentry.captureApiError({
      error,
      path,
      method: callType,
      requestBody: init,
      apiName,
      handled: errorAs.status === 400 || errorAs.status === 403,
    });

    if (
      (errorAs.status && errorAs.status === 403 && errorAs.message === this.awsAPIWrapper.unauthMsg) ||
      (errorAs.result?.status === 403 && errorAs.result?.message === this.awsAPIWrapper.unauthMsg)
    ) {
      this._messageService.add({
        severity: 'error',
        summary: errorAs.result?.message ?? 'You are unauthorized',
        life: 2000,
      });
      this.awsAPIWrapper.forceLogout();
    }

    if (options?.errorCallback) {
      options.errorCallback(error);
      return of([]);
    } else {
      if (errorAs.status === 500 ) {
        this._messageService.add({
          severity: 'error',
          summary: errorAs.result?.message ?? 'Server Error 500',
          life: 2000,
        });
      }
      return throwError(error);
    }
  };
}

@Injectable({
  providedIn: 'root',
})
export class ApiService extends APIWrapperClass {

  constructor() {
    const awsAPIWrapper: AwsApiWrapperService = inject(AwsApiWrapperService);

    super(awsAPIWrapper);
  }

  get<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiGet<T>(apiName, path, init, options);
  }
  post<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiPost<T>(apiName, path, init, options);
  }
  patch<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiPatch<T>(apiName, path, init, options);
  }
  put<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiPut<T>(apiName, path, init, options);
  }
  del<T>(apiName: string, path: string, init?: any, options?: APIOptionsInterface): Observable<T & GenericResponseResult> {
    return this._apiDel<T>(apiName, path, init, options);
  }

  toggleService(): void {
    // if (!this.env.production) {
    //   this.simulateError = !this.simulateError;
    //   console.log(`Simulated error toggled: ${this.simulateError}`);
    // } else {
    //   console.warn('toggleService is disabled in production');
    // }
  }
}
